'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2SummaryResponse } from '@/lib/pr-v2/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updatePrV2Description } from './fetch-pr-v2';
import { prV2DiscussionQueryKey, prV2SummaryQueryKey } from './query-keys';

// Coerces unknown metadata blobs into plain records for safe shallow merging.
import { asRecord } from '@/lib/pr-v2/utils/as-record';
type MutationContext = {
  previousSummary: PrV2SummaryResponse | undefined;
  summaryKey: ReturnType<typeof prV2SummaryQueryKey>;
  discussionKey: ReturnType<typeof prV2DiscussionQueryKey>;
};

/**
 * Writes updated PR body into summary metadata while preserving existing fields.
 */
function withUpdatedDescription(current: PrV2SummaryResponse | undefined, body: string): PrV2SummaryResponse | undefined {
  if (!current) {
    return current;
  }
  const touchedAt = new Date().toISOString();
  const metadataRecord = asRecord(current.pullRequest.metadata) ?? {};
  const pullRequestMetadata = asRecord(metadataRecord.pullRequest) ?? {};
  return {
    ...current,
    pullRequest: {
      ...current.pullRequest,
      githubUpdatedAt: touchedAt,
      metadataSummary: {
        ...current.pullRequest.metadataSummary,
        body,
        updatedAt: touchedAt
      },
      metadata: {
        ...metadataRecord,
        pullRequest: {
          ...pullRequestMetadata,
          body,
          updated_at: touchedAt
        }
      }
    }
  };
}
export function usePrV2UpdateDescription(owner, repo, number) {
  const $ = _c(31);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== number || $[1] !== owner || $[2] !== repo) {
    t0 = prV2SummaryQueryKey(owner, repo, number);
    $[0] = number;
    $[1] = owner;
    $[2] = repo;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const summaryKey = t0;
  let t1;
  if ($[4] !== number || $[5] !== owner || $[6] !== repo) {
    t1 = prV2DiscussionQueryKey(owner, repo, number);
    $[4] = number;
    $[5] = owner;
    $[6] = repo;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const discussionKey = t1;
  let t2;
  if ($[8] !== number || $[9] !== owner || $[10] !== repo) {
    t2 = body => updatePrV2Description(owner, repo, number, body.trim());
    $[8] = number;
    $[9] = owner;
    $[10] = repo;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== discussionKey || $[13] !== queryClient || $[14] !== summaryKey) {
    t3 = async body_0 => {
      const normalizedBody = body_0.trim();
      await queryClient.cancelQueries({
        queryKey: summaryKey
      });
      const previousSummary = queryClient.getQueryData(summaryKey);
      queryClient.setQueryData(summaryKey, current => withUpdatedDescription(current, normalizedBody));
      return {
        previousSummary,
        summaryKey,
        discussionKey
      };
    };
    $[12] = discussionKey;
    $[13] = queryClient;
    $[14] = summaryKey;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== queryClient) {
    t4 = (_error, _body, context) => {
      if (!context) {
        return;
      }
      if (context?.previousSummary !== undefined) {
        queryClient.setQueryData(context.summaryKey, context.previousSummary);
      } else {
        queryClient.removeQueries({
          queryKey: context.summaryKey,
          exact: true
        });
      }
    };
    $[16] = queryClient;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== queryClient || $[19] !== summaryKey) {
    t5 = (t6, _variables, context_0) => {
      const {
        body: body_1
      } = t6;
      const targetSummaryKey = context_0?.summaryKey ?? summaryKey;
      queryClient.setQueryData(targetSummaryKey, current_0 => withUpdatedDescription(current_0, body_1));
    };
    $[18] = queryClient;
    $[19] = summaryKey;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== discussionKey || $[22] !== queryClient || $[23] !== summaryKey) {
    t6 = async (_data, _error_0, _variables_0, context_1) => {
      const targetSummaryKey_0 = context_1?.summaryKey ?? summaryKey;
      const targetDiscussionKey = context_1?.discussionKey ?? discussionKey;
      await Promise.all([queryClient.invalidateQueries({
        queryKey: targetSummaryKey_0
      }), queryClient.invalidateQueries({
        queryKey: targetDiscussionKey
      })]);
    };
    $[21] = discussionKey;
    $[22] = queryClient;
    $[23] = summaryKey;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== t2 || $[26] !== t3 || $[27] !== t4 || $[28] !== t5 || $[29] !== t6) {
    t7 = {
      mutationFn: t2,
      onMutate: t3,
      onError: t4,
      onSuccess: t5,
      onSettled: t6
    };
    $[25] = t2;
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  return useMutation(t7);
}